import { MapPin, CheckCircle, ArrowRight, Truck, Clock, Package } from 'lucide-react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function CoveragePage() {
  const gtaCities = [
    'Toronto', 'Mississauga', 'Brampton', 'Markham', 'Vaughan', 'Richmond Hill',
    'Oakville', 'Burlington', 'Pickering', 'Ajax', 'Whitby', 'Oshawa',
    'Milton', 'Newmarket', 'Aurora', 'Stouffville', 'King City', 'Caledon'
  ];

  const ontarioRegions = [
    { name: 'Greater Toronto Area', coverage: '100%', cities: 'All major GTA cities', delivery: 'Same-day available' },
    { name: 'Golden Horseshoe', coverage: '100%', cities: 'Hamilton, Niagara, Kitchener', delivery: 'Next-day standard' },
    { name: 'Niagara Region', coverage: '95%', cities: 'St. Catharines, Niagara Falls', delivery: 'Next-day standard' },
    { name: 'Ottawa Region', coverage: '90%', cities: 'Ottawa, Gatineau area', delivery: '1-2 days' },
    { name: 'Southwestern Ontario', coverage: '85%', cities: 'London, Windsor, Sarnia', delivery: '1-2 days' },
    { name: 'Northern Ontario', coverage: '70%', cities: 'Sudbury, Thunder Bay', delivery: '2-3 days' }
  ];

  const serviceHighlights = [
    {
      icon: Clock,
      title: 'Same-Day Delivery',
      description: 'Available for most GTA locations when ordered before 2 PM',
      regions: ['Toronto', 'Mississauga', 'Brampton', 'Markham', 'Vaughan']
    },
    {
      icon: Truck,
      title: 'Next-Day Service',
      description: 'Guaranteed next-business-day delivery across Golden Horseshoe',
      regions: ['Hamilton', 'Burlington', 'Oakville', 'Kitchener', 'Waterloo']
    },
    {
      icon: Package,
      title: 'Express Routes',
      description: 'Expedited service to major Ontario cities',
      regions: ['Ottawa', 'London', 'Windsor', 'Kingston', 'Barrie']
    }
  ];

  return (
    <Layout>
      {/* Hero Section with Image */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-hero z-10" />
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1565793298595-6a879b1d9492?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3N8ZW58MHx8fHwxNzc4MTA4MjA2fDA&ixlib=rb-4.1.0&q=85)'
            }}
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 blur-[120px] rounded-full z-10" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-slide-up">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
              Service Area
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Serving <span className="text-gradient">GTA & Ontario</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Comprehensive coverage across the Greater Toronto Area and major Ontario regions. Fast, reliable deliveries wherever you need them. Our extensive network ensures your packages reach their destination on time, every time.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50 text-center">
              <div className="text-3xl font-bold text-gradient mb-2">18+</div>
              <div className="text-sm text-muted-foreground">GTA Cities</div>
            </Card>
            <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50 text-center">
              <div className="text-3xl font-bold text-gradient mb-2">6</div>
              <div className="text-sm text-muted-foreground">Major Regions</div>
            </Card>
            <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50 text-center">
              <div className="text-3xl font-bold text-gradient mb-2">100%</div>
              <div className="text-sm text-muted-foreground">GTA Coverage</div>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Coverage Areas */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start mb-24">
            {/* GTA Cities */}
            <Card className="scroll-animate p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-gradient-primary">
                  <MapPin className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold">GTA Coverage</h3>
                  <p className="text-sm text-muted-foreground">18+ Major Cities</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {gtaCities.map((city, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium">{city}</span>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm">
                  <span className="font-semibold text-primary">Same-day delivery available</span> for most GTA locations when ordered before 2 PM. Weekend and evening deliveries can be arranged.
                </p>
              </div>
            </Card>

            {/* Ontario Regions */}
            <Card className="scroll-animate p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all duration-300" style={{ animationDelay: '0.15s' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-secondary/20 border border-secondary/30">
                  <MapPin className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold">Ontario Regions</h3>
                  <p className="text-sm text-muted-foreground">Province-wide Service</p>
                </div>
              </div>
              
              <div className="space-y-6">
                {ontarioRegions.map((region, index) => (
                  <div key={index} className="group">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{region.name}</span>
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
                        {region.coverage}
                      </Badge>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2 overflow-hidden mb-2">
                      <div 
                        className="h-full bg-gradient-primary rounded-full transition-all duration-500 group-hover:shadow-glow"
                        style={{ width: region.coverage }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>Coverage: {region.cities}</div>
                      <div>Delivery: {region.delivery}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Coverage Map Image */}
          <div className="scroll-animate mb-24">
            <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50">
              <img 
                src="https://images.pexels.com/photos/35501717/pexels-photo-35501717.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Delivery Coverage Map"
                className="w-full h-96 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-semibold mb-3">Expanding Our Reach</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We're continuously expanding our service areas across Ontario. Our strategic network of distribution centers and partnerships ensure efficient delivery routes and faster service times. Contact us to inquire about service availability in your area.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Highlights */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 scroll-animate">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Delivery <span className="text-gradient">Options</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Flexible delivery options to meet your timeline and budget requirements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {serviceHighlights.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index}
                  className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-glow group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="p-3 rounded-xl bg-gradient-primary inline-flex mb-4 group-hover:shadow-glow transition-all">
                    <Icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">{service.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{service.description}</p>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Available in:</div>
                    {service.regions.map((region, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        {region}
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Delivery Vehicle Image */}
          <div className="scroll-animate">
            <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50">
              <div className="grid md:grid-cols-2 gap-0">
                <img 
                  src="https://images.unsplash.com/photo-1543499459-d1460946bdc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMHBlcnNvbnxlbnwwfHx8fDE3NzgxMDgyMTJ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Professional Delivery Service"
                  className="w-full h-full object-cover"
                />
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h3 className="text-2xl font-semibold mb-4">Professional Delivery Team</h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    Our experienced delivery professionals are trained to handle packages with care and professionalism. Every driver is fully licensed, insured, and background-checked to ensure the safety and security of your shipments.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-sm">Licensed and insured drivers</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-sm">Professional uniforms and identification</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-sm">Real-time GPS tracking</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Expansion Notice */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <Card className="scroll-animate p-12 bg-gradient-card backdrop-blur-sm border-primary/20 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Not Sure If We <span className="text-gradient">Serve Your Area?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                We're continuously expanding our coverage across Ontario. Even if your location isn't listed, contact us to discuss your delivery needs. We can often accommodate special requests and are always looking to expand into new areas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/quote">
                  <Button size="lg" className="bg-gradient-primary hover:shadow-glow-lg transition-all duration-300 w-full sm:w-auto">
                    Request Service Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="tel:(647)-642-0062">
                  <Button size="lg" variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 w-full sm:w-auto">
                    Call Us: (647)-642-0062
                  </Button>
                </a>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
